import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Smartphone, Eye, EyeOff, Share2, Clock, Users, Trash2, Bell, Gift, CalendarDays, Banknote, Target, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mirrors REFERRAL_BOUNDS in backend/referral.py. Kept client-side too so a typo is
// caught before the round trip; the backend stays the authority and its 400 message is
// what gets shown when the two ever disagree.
const REFERRAL_BOUNDS = {
  referral_reward_threshold: [1, 20],
  referral_gift_wash_count: [1, 10],
  referral_gift_validity_days: [1, 365],
  referral_reward_value_ron: [0, 100000],
};

export default function AppSettings() {
  const { language } = useLanguage();
  const { token } = useAuth();
  const isRo = language === 'ro';

  const [showWashHistory, setShowWashHistory] = useState(false);
  const [allowShare, setAllowShare] = useState(false);
  const [qrValidityMinutes, setQrValidityMinutes] = useState(60);
  const [qrValidityInput, setQrValidityInput] = useState('60');
  const [loading, setLoading] = useState(true);
  // Distinct from `loading`: the page renders even when the settings fetch failed, and
  // then every value on screen is a hardcoded default rather than what is stored. Saving
  // anything in that state would write the placeholder show_wash_history along with it
  // (see saveSettings) and silently flip the mobile app's wash history.
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [saving, setSaving] = useState(null);
  const [notifySubscription, setNotifySubscription] = useState(true);
  const [googlePayEnabled, setGooglePayEnabled] = useState(true);
  const [applePayEnabled, setApplePayEnabled] = useState(true);
  const [referralsEnabled, setReferralsEnabled] = useState(false);
  // Campaign values are held as the raw input strings, like qrValidityInput above: the
  // field has to stay editable while empty or half-typed, so it cannot be bound to a
  // number. The parsed value is only ever needed at save time.
  const [rewardThresholdInput, setRewardThresholdInput] = useState('1');
  const [giftWashCountInput, setGiftWashCountInput] = useState('2');
  const [giftValidityDaysInput, setGiftValidityDaysInput] = useState('30');
  const [rewardValueRonInput, setRewardValueRonInput] = useState('300');
  // Forced update. Empty min version = off (the backend stores null).
  const [minAppVersionInput, setMinAppVersionInput] = useState('');
  const [iosStoreUrlInput, setIosStoreUrlInput] = useState('');
  const [androidStoreUrlInput, setAndroidStoreUrlInput] = useState('');
  const [operators, setOperators] = useState([]);
  const [newOperatorName, setNewOperatorName] = useState('');
  const [operatorLoading, setOperatorLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchOperators();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/app-settings`);
      // A failed GET can still answer with a perfectly valid JSON body (a 500 detail, an
      // error page from a proxy). Without this check every `?? default` below fired and
      // settingsLoaded was set to true anyway, so the page presented a full set of
      // hardcoded defaults as if they were what is stored — and the save path then wrote
      // them back over the real settings.
      if (!res.ok) throw new Error();
      const data = await res.json();
      setShowWashHistory(data.show_wash_history ?? false);
      setAllowShare(data.allow_share ?? false);
      setNotifySubscription(data.notify_subscription ?? true);
      setGooglePayEnabled(data.google_pay_enabled ?? true);
      setApplePayEnabled(data.apple_pay_enabled ?? true);
      setReferralsEnabled(data.referrals_enabled ?? false);
      setRewardThresholdInput(String(data.referral_reward_threshold ?? 1));
      setGiftWashCountInput(String(data.referral_gift_wash_count ?? 2));
      setGiftValidityDaysInput(String(data.referral_gift_validity_days ?? 30));
      setRewardValueRonInput(String(data.referral_reward_value_ron ?? 300));
      setMinAppVersionInput(data.min_app_version ?? '');
      setIosStoreUrlInput(data.ios_store_url ?? '');
      setAndroidStoreUrlInput(data.android_store_url ?? '');
      const validity = data.qr_validity_minutes ?? 60;
      setQrValidityMinutes(validity);
      setQrValidityInput(String(validity));
      setSettingsLoaded(true);
    } catch {
      toast.error(isRo ? 'Eroare la încărcarea setărilor' : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchOperators = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/operators`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setOperators(data);
    } catch (err) {
      console.error('Error fetching operators:', err);
      toast.error(isRo ? 'Eroare la încărcarea operatorilor' : 'Failed to load operators');
    }
  };

  const handleAddOperator = async () => {
    const name = newOperatorName.trim();
    if (!name) return;
    setOperatorLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/operators`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Eroare');
      }
      setNewOperatorName('');
      await fetchOperators();
    } catch (err) {
      toast.error(err.message || (isRo ? 'Eroare la adăugarea operatorului' : 'Failed to add operator'));
    } finally {
      setOperatorLoading(false);
    }
  };

  const handleDeleteOperator = async (id) => {
    if (!window.confirm(isRo ? 'Ștergi acest operator?' : 'Delete this operator?')) return;
    setOperatorLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/operators/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Eroare');
      }
      await fetchOperators();
    } catch (err) {
      toast.error(err.message || (isRo ? 'Eroare la ștergerea operatorului' : 'Failed to delete operator'));
    } finally {
      setOperatorLoading(false);
    }
  };

  // Sends ONLY the fields that changed. The backend PUT applies exclude_unset, so a field
  // that is not sent keeps its stored value. Serialising the whole form instead meant
  // every click rewrote all the other settings from whatever this page happened to hold —
  // including referrals_enabled, so one click on an unrelated toggle could stop a live
  // campaign, and the backend's exclude_unset protection never applied to it at all.
  //
  // show_wash_history rides along on every request because it is the only field
  // AppSettingsUpdate declares without a default — a body without it is rejected as 422
  // before any handler runs. It is resent with the value currently on screen, which is
  // exactly why every caller must pass requireSettingsLoaded() first.
  //
  // Returns whether the write actually landed; the callers own the optimistic state and
  // must not apply it on false.
  const saveSettings = async (changes, savingKey) => {
    setSaving(savingKey);
    try {
      const res = await fetch(`${API_URL}/api/admin/app-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ show_wash_history: showWashHistory, ...changes }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        // The endpoint's own 400 carries a Romanian sentence naming the field and its
        // range — that is the message worth showing. FastAPI's 422 puts a list of
        // validation objects in the same key, which is not readable, so it falls through
        // to the generic message below.
        const rejected = new Error(typeof err.detail === 'string' ? err.detail : '');
        // Marked, so only a message the SERVER wrote reaches the admin. A network failure
        // lands in the same catch with an untranslated browser string ("Failed to fetch"),
        // which is not something to show a Romanian operator.
        rejected.fromServer = true;
        throw rejected;
      }
      return true;
    } catch (err) {
      toast.error((err.fromServer && err.message)
        || (isRo ? 'Eroare la salvarea setărilor' : 'Failed to save settings'));
      return false;
    } finally {
      setSaving(null);
    }
  };

  // No write may leave a page whose settings never loaded: every value on screen is then
  // a hardcoded default, and each save resends show_wash_history along with the change.
  const requireSettingsLoaded = () => {
    if (settingsLoaded) return true;
    toast.error(isRo
      ? 'Setările nu s-au încărcat. Reîncarcă pagina înainte de a salva.'
      : 'Settings failed to load. Reload the page before saving.');
    return false;
  };

  const handleToggle = async (field, newValue) => {
    if (!requireSettingsLoaded()) return;
    const saved = await saveSettings({ [field]: newValue }, field);
    // The server refused the write, so the switch stays where the server left it. It used
    // to flip regardless and announce success on top of the error toast — two
    // contradictory toasts, and a page showing a state that was never stored.
    if (!saved) return;
    if (field === 'show_wash_history') {
      setShowWashHistory(newValue);
      toast.success(
        newValue
          ? (isRo ? 'Ultimele spălări sunt acum vizibile în aplicație' : 'Wash history is now visible in the app')
          : (isRo ? 'Ultimele spălări sunt acum ascunse din aplicație' : 'Wash history is now hidden from the app')
      );
    } else if (field === 'allow_share') {
      setAllowShare(newValue);
      toast.success(
        newValue
          ? (isRo ? 'Partajarea QR este acum activată în aplicație' : 'QR sharing is now enabled in the app')
          : (isRo ? 'Partajarea QR este acum dezactivată din aplicație' : 'QR sharing is now disabled from the app')
      );
    } else if (field === 'notify_subscription') {
      setNotifySubscription(newValue);
      toast.success(
        newValue
          ? (isRo ? 'Notificările de abonament sunt acum activate' : 'Subscription notifications are now enabled')
          : (isRo ? 'Notificările de abonament sunt acum dezactivate' : 'Subscription notifications are now disabled')
      );
    } else if (field === 'google_pay_enabled') {
      setGooglePayEnabled(newValue);
      toast.success(
        newValue
          ? (isRo ? 'Google Pay este acum activat' : 'Google Pay is now enabled')
          : (isRo ? 'Google Pay este acum dezactivat' : 'Google Pay is now disabled')
      );
    } else if (field === 'apple_pay_enabled') {
      setApplePayEnabled(newValue);
      toast.success(
        newValue
          ? (isRo ? 'Apple Pay este acum activat' : 'Apple Pay is now enabled')
          : (isRo ? 'Apple Pay este acum dezactivat' : 'Apple Pay is now disabled')
      );
    } else if (field === 'referrals_enabled') {
      setReferralsEnabled(newValue);
      toast.success(
        newValue
          ? (isRo ? 'Recomandările sunt acum active' : 'Referrals are now enabled')
          : (isRo ? 'Recomandările sunt acum oprite' : 'Referrals are now disabled')
      );
    }
  };

  const handleSaveValidity = async () => {
    if (!requireSettingsLoaded()) return;
    const minutes = parseInt(qrValidityInput, 10);
    if (isNaN(minutes) || minutes < 1) {
      toast.error(isRo ? 'Introduceți un număr valid de minute (minim 1)' : 'Enter a valid number of minutes (min 1)');
      return;
    }
    const saved = await saveSettings({ qr_validity_minutes: minutes }, 'qr_validity');
    if (!saved) return;
    setQrValidityMinutes(minutes);
    toast.success(isRo ? `Validitate QR salvată: ${minutes} minute` : `QR validity saved: ${minutes} minutes`);
  };

  const handleSaveReferralSetting = async (field, rawInput, setInput, successMessage) => {
    if (!requireSettingsLoaded()) return;
    const [min, max] = REFERRAL_BOUNDS[field];
    const value = parseInt(rawInput, 10);
    if (isNaN(value) || value < min || value > max) {
      toast.error(isRo
        ? `Introduceți o valoare între ${min} și ${max}`
        : `Enter a value between ${min} and ${max}`);
      return;
    }
    const saved = await saveSettings({ [field]: value }, field);
    if (!saved) return;
    // Write the parsed number back into the field so what stays on screen is what was
    // actually stored — "007" or "7 " would otherwise keep reading as typed.
    setInput(String(value));
    toast.success(successMessage(value));
  };

  // Checked here as well as on the backend only because the backend's 422 is not readable
  // (see saveSettings); the backend stays the authority.
  const handleSaveForcedUpdate = async (field, rawInput, setInput) => {
    if (!requireSettingsLoaded()) return;
    const value = rawInput.trim();
    if (field === 'min_app_version') {
      if (value && !/^\d+\.\d+\.\d+$/.test(value)) {
        toast.error(isRo ? 'Versiunea trebuie să fie de forma X.Y.Z (ex. 2.0.5)' : 'Version must look like X.Y.Z (e.g. 2.0.5)');
        return;
      }
    } else if (!value.startsWith('https://')) {
      toast.error(isRo ? 'Linkul trebuie să înceapă cu https://' : 'The link must start with https://');
      return;
    }
    const saved = await saveSettings({ [field]: value || null }, field);
    if (!saved) return;
    setInput(value);
    toast.success(field === 'min_app_version' && !value
      ? (isRo ? 'Actualizarea obligatorie este oprită' : 'Forced update is off')
      : (isRo ? 'Salvat' : 'Saved'));
  };

  const forcedUpdateFields = [
    {
      field: 'min_app_version',
      label: isRo ? 'Versiune minimă' : 'Minimum version',
      description: isRo
        ? 'Aplicațiile mai vechi de această versiune sunt blocate până la actualizare. Gol = oprit.'
        : 'Apps older than this version are blocked until updated. Empty = off.',
      warning: 'Setează doar o versiune deja aprobată în ambele magazine — altfel blochezi toți utilizatorii.',
      placeholder: 'ex. 2.0.5',
      width: 'w-28',
      input: minAppVersionInput,
      setInput: setMinAppVersionInput,
    },
    {
      field: 'ios_store_url',
      label: isRo ? 'Link App Store' : 'App Store link',
      description: isRo ? 'Unde duce butonul „Actualizează” pe iPhone' : 'Where the update button leads on iPhone',
      width: 'w-72',
      input: iosStoreUrlInput,
      setInput: setIosStoreUrlInput,
    },
    {
      field: 'android_store_url',
      label: isRo ? 'Link Google Play' : 'Google Play link',
      description: isRo ? 'Unde duce butonul „Actualizează” pe Android' : 'Where the update button leads on Android',
      width: 'w-72',
      input: androidStoreUrlInput,
      setInput: setAndroidStoreUrlInput,
    },
  ];

  // The four campaign values, in the order the offer reads: how many friends it takes,
  // then how many washes they earn, for how long, worth how much. Described once here so
  // the rows below stay identical.
  const referralFields = [
    {
      field: 'referral_reward_threshold',
      icon: Target,
      label: isRo ? 'Prag recomandări' : 'Referral threshold',
      description: isRo
        ? 'Câte recomandări reușite trebuie să adune un utilizator pentru a primi recompensa. 1 = recompensă la fiecare prieten.'
        : 'How many successful referrals a user must collect before receiving the reward. 1 = a reward for every friend.',
      unit: isRo ? 'recomandări' : 'referrals',
      input: rewardThresholdInput,
      setInput: setRewardThresholdInput,
      success: (v) => (isRo ? `Prag recomandări salvat: ${v}` : `Referral threshold saved: ${v}`),
    },
    {
      field: 'referral_gift_wash_count',
      icon: Gift,
      label: isRo ? 'Spălări adăugate' : 'Washes added',
      description: isRo
        ? 'Câte spălări se adaugă pe abonamentul celui care recomandă (sau pe un abonament nou de același tip, dacă nu mai are unul activ). Doar cine are sau a avut un abonament primește recompensa.'
        : 'How many washes are added to the referrer\'s subscription (or to a new one of the same type if none is active). Only referrers who have or had a subscription are rewarded.',
      unit: isRo ? 'spălări' : 'washes',
      input: giftWashCountInput,
      setInput: setGiftWashCountInput,
      success: (v) => (isRo ? `Spălări adăugate salvate: ${v}` : `Washes added saved: ${v}`),
    },
    {
      field: 'referral_gift_validity_days',
      icon: CalendarDays,
      label: isRo ? 'Zile adăugate la abonament' : 'Days added to the subscription',
      description: isRo
        ? 'Cu câte zile se prelungește abonamentul celui care recomandă; un abonament nou creat ca recompensă e valabil atâtea zile.'
        : 'How many days the referrer\'s subscription is extended by; a subscription created as the reward is valid for that many days.',
      unit: isRo ? 'zile' : 'days',
      input: giftValidityDaysInput,
      setInput: setGiftValidityDaysInput,
      success: (v) => (isRo ? `Zile adăugate salvate: ${v} zile` : `Days added saved: ${v} days`),
    },
    {
      field: 'referral_reward_value_ron',
      icon: Banknote,
      label: isRo ? 'Valoare recompensă' : 'Reward value',
      description: isRo
        ? 'Valoarea afișată în aplicație pentru recompensă („în valoare de ... lei”). Nu se facturează, e doar text.'
        : 'The value the app quotes for the reward. Nothing is charged — it is copy only.',
      unit: isRo ? 'lei' : 'RON',
      input: rewardValueRonInput,
      setInput: setRewardValueRonInput,
      success: (v) => (isRo ? `Valoare recompensă salvată: ${v} lei` : `Reward value saved: ${v} RON`),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Smartphone className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {isRo ? 'Setări Aplicație Mobilă' : 'Mobile App Settings'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isRo
              ? 'Controlează ce secțiuni sunt vizibile în aplicația mobilă'
              : 'Control which sections are visible in the mobile app'}
          </p>
        </div>
      </div>

      {/* Settings card */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-4">
        <h2 className="text-base font-semibold text-foreground">
          {isRo ? 'Vizibilitate secțiuni' : 'Section visibility'}
        </h2>

        {/* Toggle: Ultimele spălări */}
        <div className="flex items-center justify-between py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${showWashHistory ? 'bg-primary/10' : 'bg-muted'}`}>
              {showWashHistory
                ? <Eye className="w-4 h-4 text-primary" />
                : <EyeOff className="w-4 h-4 text-muted-foreground" />
              }
            </div>
            <div>
              <p className="font-medium text-foreground">
                {isRo ? 'Ultimele spălări' : 'Recent washes'}
              </p>
              <p className="text-sm text-muted-foreground">
                {isRo
                  ? 'Afișează istoricul spălărilor pe ecranul principal al aplicației'
                  : 'Show wash history on the app home screen'}
              </p>
            </div>
          </div>
          <button
            onClick={() => saving === null && handleToggle('show_wash_history', !showWashHistory)}
            disabled={saving !== null || !settingsLoaded}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background disabled:opacity-50 ${
              showWashHistory ? 'bg-primary' : 'bg-muted-foreground/30'
            }`}
            aria-checked={showWashHistory}
            role="switch"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                showWashHistory ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Toggle: Partajare QR */}
        <div className={`flex items-center justify-between py-4 border-b border-border ${allowShare ? '' : 'last:border-0'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${allowShare ? 'bg-primary/10' : 'bg-muted'}`}>
              <Share2 className={`w-4 h-4 ${allowShare ? 'text-primary' : 'text-muted-foreground'}`} />
            </div>
            <div>
              <p className="font-medium text-foreground">
                {isRo ? 'Partajare QR' : 'QR Sharing'}
              </p>
              <p className="text-sm text-muted-foreground">
                {isRo
                  ? 'Permite utilizatorilor să partajeze codul QR cu alte persoane'
                  : 'Allow users to share their QR code with other people'}
              </p>
            </div>
          </div>
          <button
            onClick={() => saving === null && handleToggle('allow_share', !allowShare)}
            disabled={saving !== null || !settingsLoaded}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background disabled:opacity-50 ${
              allowShare ? 'bg-primary' : 'bg-muted-foreground/30'
            }`}
            aria-checked={allowShare}
            role="switch"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                allowShare ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Toggle: Notificări abonament */}
        <div className="flex items-center justify-between py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${notifySubscription ? 'bg-primary/10' : 'bg-muted'}`}>
              <Bell className={`w-4 h-4 ${notifySubscription ? 'text-primary' : 'text-muted-foreground'}`} />
            </div>
            <div>
              <p className="font-medium text-foreground">
                {isRo ? 'Notificări abonament' : 'Subscription notifications'}
              </p>
              <p className="text-sm text-muted-foreground">
                {isRo
                  ? 'Utilizatorii primesc un popup când le-a mai rămas 1 spălare sau abonamentul expiră în mai puțin de 24 de ore'
                  : 'Users receive a popup when 1 wash remains or their subscription expires in under 24 hours'}
              </p>
            </div>
          </div>
          <button
            onClick={() => saving === null && handleToggle('notify_subscription', !notifySubscription)}
            disabled={saving !== null || !settingsLoaded}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background disabled:opacity-50 ${
              notifySubscription ? 'bg-primary' : 'bg-muted-foreground/30'
            }`}
            aria-checked={notifySubscription}
            role="switch"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                notifySubscription ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Toggle: Google Pay */}
        <div className="flex items-center justify-between py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${googlePayEnabled ? 'bg-primary/10' : 'bg-muted'}`}>
              <Smartphone className={`w-4 h-4 ${googlePayEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
            </div>
            <div>
              <p className="font-medium text-foreground">Google Pay</p>
              <p className="text-sm text-muted-foreground">
                {isRo ? 'Permite plata cu Google Pay în aplicație' : 'Allow paying with Google Pay in the app'}
              </p>
            </div>
          </div>
          <button
            onClick={() => saving === null && handleToggle('google_pay_enabled', !googlePayEnabled)}
            disabled={saving !== null || !settingsLoaded}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background disabled:opacity-50 ${
              googlePayEnabled ? 'bg-primary' : 'bg-muted-foreground/30'
            }`}
            aria-checked={googlePayEnabled}
            role="switch"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
              googlePayEnabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {/* Toggle: Apple Pay */}
        <div className="flex items-center justify-between py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${applePayEnabled ? 'bg-primary/10' : 'bg-muted'}`}>
              <Smartphone className={`w-4 h-4 ${applePayEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
            </div>
            <div>
              <p className="font-medium text-foreground">Apple Pay</p>
              <p className="text-sm text-muted-foreground">
                {isRo ? 'Permite plata cu Apple Pay în aplicație' : 'Allow paying with Apple Pay in the app'}
              </p>
            </div>
          </div>
          <button
            onClick={() => saving === null && handleToggle('apple_pay_enabled', !applePayEnabled)}
            disabled={saving !== null || !settingsLoaded}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background disabled:opacity-50 ${
              applePayEnabled ? 'bg-primary' : 'bg-muted-foreground/30'
            }`}
            aria-checked={applePayEnabled}
            role="switch"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
              applePayEnabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {/* Toggle: Referrals — pornește/oprește întreaga campanie de recomandări */}
        <div className="flex items-center justify-between py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${referralsEnabled ? 'bg-primary/10' : 'bg-muted'}`}>
              <Users className={`w-4 h-4 ${referralsEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
            </div>
            <div>
              <p className="font-medium text-foreground">
                {isRo ? 'Recomandări (Referrals)' : 'Referrals'}
              </p>
              <p className="text-sm text-muted-foreground">
                {isRo
                  ? 'Afișează ecranul „Invită un prieten” în aplicație și permite acordarea cadourilor. Oprit, codurile deja emise nu mai pot fi folosite.'
                  : 'Shows the invite screen in the app and allows gifts to be granted. Turned off, existing codes stop working.'}
              </p>
            </div>
          </div>
          <button
            onClick={() => saving === null && handleToggle('referrals_enabled', !referralsEnabled)}
            disabled={saving !== null || !settingsLoaded}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background disabled:opacity-50 ${
              referralsEnabled ? 'bg-primary' : 'bg-muted-foreground/30'
            }`}
            aria-checked={referralsEnabled}
            role="switch"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
              referralsEnabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {/* Campanie recomandări — valorile cu care se emit recompensele.
            Vizibile chiar și cu recomandările oprite, ca ele să poată fi setate ÎNAINTE
            de pornirea campaniei, nu după ce au plecat deja cadouri pe valorile implicite. */}
        <div className="py-4 border-b border-border space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-primary/10 shrink-0">
              <Gift className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="font-medium text-foreground">
                {isRo ? 'Campanie recomandări' : 'Referral campaign'}
              </p>
              <p className="text-sm text-muted-foreground">
                {isRo
                  ? 'Modificările se aplică doar recompenselor viitoare. Cadourile deja acordate păstrează numărul de spălări și data de expirare cu care au fost emise.'
                  : 'Changes apply to future rewards only. Gifts already granted keep the wash count and expiry they were issued with.'}
              </p>
            </div>
          </div>

          {referralFields.map(({ field, icon: Icon, label, description, unit, input, setInput, success }) => (
            <div key={field} className="flex items-center justify-between gap-4 pl-12">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-muted shrink-0">
                  <Icon className="w-4 h-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{label}</p>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <input
                  type="number"
                  min={REFERRAL_BOUNDS[field][0]}
                  max={REFERRAL_BOUNDS[field][1]}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && saving === null && handleSaveReferralSetting(field, input, setInput, success)}
                  disabled={!settingsLoaded}
                  className="w-20 rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground text-center focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                  data-testid={`input-${field}`}
                />
                <span className="text-sm text-muted-foreground">{unit}</span>
                <button
                  onClick={() => handleSaveReferralSetting(field, input, setInput, success)}
                  disabled={saving !== null || !settingsLoaded}
                  className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
                  data-testid={`save-${field}`}
                >
                  {saving === field
                    ? (isRo ? 'Se salvează...' : 'Saving...')
                    : (isRo ? 'Salvează' : 'Save')}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Actualizare obligatorie — blochează versiunile vechi ale aplicației */}
        <div className="py-4 border-b border-border space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-primary/10 shrink-0">
              <RefreshCw className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="font-medium text-foreground">
                {isRo ? 'Actualizare obligatorie' : 'Forced update'}
              </p>
              <p className="text-sm text-muted-foreground">
                {isRo
                  ? 'Aplicația verifică la pornire și la revenirea în prim-plan.'
                  : 'The app checks on launch and when it returns to the foreground.'}
              </p>
            </div>
          </div>

          {forcedUpdateFields.map(({ field, label, description, warning, placeholder, width, input, setInput }) => (
            <div key={field} className="flex items-center justify-between gap-4 pl-12">
              <div>
                <p className="font-medium text-foreground">{label}</p>
                <p className="text-sm text-muted-foreground">{description}</p>
                {warning && <p className="text-sm font-medium text-destructive">{warning}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <input
                  type="text"
                  value={input}
                  placeholder={placeholder}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && saving === null && handleSaveForcedUpdate(field, input, setInput)}
                  disabled={!settingsLoaded}
                  className={`${width} rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50`}
                  data-testid={`input-${field}`}
                />
                <button
                  onClick={() => handleSaveForcedUpdate(field, input, setInput)}
                  disabled={saving !== null || !settingsLoaded}
                  className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
                  data-testid={`save-${field}`}
                >
                  {saving === field
                    ? (isRo ? 'Se salvează...' : 'Saving...')
                    : (isRo ? 'Salvează' : 'Save')}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Validitate QR distribuit — vizibil doar când allowShare=true */}
        {allowShare && (
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-primary/10">
                <Clock className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">
                  {isRo ? 'Validitate QR distribuit' : 'Shared QR validity'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {isRo
                    ? 'Câte minute este valabil un QR distribuit de la momentul generării'
                    : 'How many minutes a shared QR is valid from the moment of generation'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                value={qrValidityInput}
                onChange={e => setQrValidityInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSaveValidity()}
                className="w-20 rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground text-center focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <span className="text-sm text-muted-foreground">{isRo ? 'min' : 'min'}</span>
              <button
                onClick={handleSaveValidity}
                disabled={saving !== null || !settingsLoaded}
                className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {saving === 'qr_validity'
                  ? (isRo ? 'Se salvează...' : 'Saving...')
                  : (isRo ? 'Salvează' : 'Save')}
              </button>
            </div>
          </div>
        )}

        {/* Operatori */}
        <div className="border-t border-border pt-6 mt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">
                {isRo ? 'Operatori' : 'Operators'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isRo
                  ? 'Operatorii apar ca opțiuni în app la cumpărarea unui abonament'
                  : 'Operators appear as options in the app when purchasing a subscription'}
              </p>
            </div>
          </div>

          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newOperatorName}
              onChange={(e) => setNewOperatorName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddOperator()}
              placeholder={isRo ? 'Nume operator...' : 'Operator name...'}
              className="flex-1 px-3 py-2 border border-border bg-background text-foreground rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              maxLength={100}
            />
            <button
              onClick={handleAddOperator}
              disabled={operatorLoading || !newOperatorName.trim()}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isRo ? 'Adaugă' : 'Add'}
            </button>
          </div>

          {operators.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              {isRo ? 'Niciun operator adăugat.' : 'No operators added yet.'}
            </p>
          ) : (
            <ul className="space-y-2">
              {operators.map((op) => (
                <li
                  key={op.id}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                    op.is_active ? 'border-border bg-muted/50' : 'border-border bg-muted/50 opacity-50'
                  }`}
                >
                  <span className="text-sm text-foreground">{op.name}</span>
                  <div className="flex items-center gap-2">
                    {!op.is_active && (
                      <span className="text-xs text-muted-foreground italic">
                        {isRo ? 'dezactivat' : 'inactive'}
                      </span>
                    )}
                    <button
                      onClick={() => handleDeleteOperator(op.id)}
                      disabled={operatorLoading}
                      className="text-destructive/70 hover:text-destructive p-1 rounded disabled:opacity-50"
                      title={isRo ? 'Șterge' : 'Delete'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Status badges */}
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">
            {showWashHistory
              ? (isRo ? '✓ Ultimele spălări vizibile în aplicație' : '✓ Recent washes visible in app')
              : (isRo ? '✗ Ultimele spălări ascunse din aplicație' : '✗ Recent washes hidden from app')}
          </p>
          <p className="text-xs text-muted-foreground">
            {allowShare
              ? (isRo ? '✓ Partajarea QR activată în aplicație' : '✓ QR sharing enabled in app')
              : (isRo ? '✗ Partajarea QR dezactivată din aplicație' : '✗ QR sharing disabled from app')}
          </p>
        </div>
      </div>
    </div>
  );
}
